/**
 * Shared utilities for compaction and branch summarization.
 */

import { isAbsolute, relative, sep } from "node:path";
import type { Message } from "@bastani/pi-ai/compat";
import type { AgentMessage } from "@earendil-works/pi-agent-core";
import { Patch } from "../tools/hashline-engine/index.ts";
import { resolveToCwd } from "../tools/path-utils.ts";
import type { LineRange } from "./compaction-types.js";

// ============================================================================
// File Operation Tracking
// ============================================================================

export interface FileOperations {
	read: Set<string>;
	written: Set<string>;
	edited: Set<string>;
}

export function createFileOps(): FileOperations {
	return {
		read: new Set(),
		written: new Set(),
		edited: new Set(),
	};
}

/**
 * Extract file operations from direct and recorded nested tool calls.
 */
export function extractFileOpsFromMessage(message: AgentMessage, fileOps: FileOperations, cwd?: string): void {
	if (message.role === "toolResult") {
		for (const call of message.nestedCalls?.calls ?? []) addFileOp(call.name, call.arguments, fileOps, cwd);
		return;
	}
	if (message.role !== "assistant") return;
	if (!("content" in message) || !Array.isArray(message.content)) return;

	for (const block of message.content) {
		if (typeof block !== "object" || block === null) continue;
		if (!("type" in block) || block.type !== "toolCall") continue;
		if (!("arguments" in block) || !("name" in block)) continue;

		addFileOp(block.name, block.arguments as Record<string, unknown> | undefined, fileOps, cwd);
	}
}

function addFileOp(
	name: string,
	args: Record<string, unknown> | undefined,
	fileOps: FileOperations,
	cwd?: string,
): void {
	if (name === "edit" && typeof args?.input === "string") {
		for (const path of hashlineEditPaths(args.input)) fileOps.edited.add(normalizeFileOpPath(path, cwd));
	}
	const path = typeof args?.path === "string" ? args.path : undefined;
	if (!path) return;
	const normalizedPath = normalizeFileOpPath(path, cwd);
	if (name === "read") fileOps.read.add(normalizedPath);
	else if (name === "write") fileOps.written.add(normalizedPath);
	else if (name === "edit") fileOps.edited.add(normalizedPath);
}

export function normalizeFileOpPath(path: string, cwd?: string): string {
	if (cwd === undefined || /^[a-z][a-z0-9+.-]*:\/\//i.test(path)) return path;
	const absolutePath = resolveToCwd(path, cwd);
	const relativePath = relative(cwd, absolutePath);
	return relativePath === ".." || relativePath.startsWith(`..${sep}`) || isAbsolute(relativePath)
		? absolutePath
		: relativePath || ".";
}

function hashlineEditPaths(input: string): string[] {
	try {
		return Patch.parse(input).sections.map((section) => section.path);
	} catch {
		return [];
	}
}

/**
 * Compute final file lists from file operations.
 * Returns readFiles (files only read, not modified) and modifiedFiles.
 */
export function computeFileLists(fileOps: FileOperations): { readFiles: string[]; modifiedFiles: string[] } {
	const modified = new Set([...fileOps.edited, ...fileOps.written]);
	const readOnly = [...fileOps.read].filter((f) => !modified.has(f)).sort();
	const modifiedFiles = [...modified].sort();
	return { readFiles: readOnly, modifiedFiles };
}

/**
 * Format file operations as XML tags for summary.
 */
export function formatFileOperations(readFiles: string[], modifiedFiles: string[]): string {
	const sections: string[] = [];
	if (readFiles.length > 0) {
		sections.push(`<read-files>\n${readFiles.join("\n")}\n</read-files>`);
	}
	if (modifiedFiles.length > 0) {
		sections.push(`<modified-files>\n${modifiedFiles.join("\n")}\n</modified-files>`);
	}
	if (sections.length === 0) return "";
	return `\n\n${sections.join("\n\n")}`;
}

// ============================================================================
// Message Serialization
// ============================================================================

/** Maximum characters for a tool result in serialized summaries. */
const TOOL_RESULT_MAX_CHARS = 2000;

/**
 * Truncate text to a maximum character length for summarization.
 * Keeps the beginning and appends a truncation marker.
 */
function truncateForSummary(text: string, maxChars: number): string {
	if (text.length <= maxChars) return text;
	const truncatedChars = text.length - maxChars;
	return `${text.slice(0, maxChars)}\n\n[... ${truncatedChars} more characters truncated]`;
}

/**
 * Serialize LLM messages to text for summarization.
 * This prevents the model from treating it as a conversation to continue.
 * Call convertToLlm() first to handle custom message types.
 *
 * Tool results are truncated to keep the summarization request within
 * reasonable token budgets. Full content is not needed for summarization.
 *
 * Thinking is omitted. Providers return summaries of the real reasoning, and
 * replayed thinking is often not part of the model's live context: for example,
 * a block that does not match its request is dropped. Including it can make the
 * summarization request larger than the context it summarizes. Visible text,
 * tool calls, and tool results record what happened.
 */
export function serializeConversation(messages: Message[]): string {
	const parts: string[] = [];

	for (const msg of messages) {
		if (msg.role === "user") {
			const content =
				typeof msg.content === "string"
					? msg.content
					: msg.content
							.filter((c): c is { type: "text"; text: string } => c.type === "text")
							.map((c) => c.text)
							.join("");
			if (content) parts.push(`[User]: ${content}`);
		} else if (msg.role === "assistant") {
			const textParts: string[] = [];
			const toolCalls: string[] = [];

			for (const block of msg.content) {
				if (block.type === "text") {
					textParts.push(block.text);
				} else if (block.type === "toolCall") {
					const args = block.arguments as Record<string, unknown>;
					const argsStr = Object.entries(args)
						.map(([k, v]) => `${k}=${JSON.stringify(v)}`)
						.join(", ");
					toolCalls.push(`${block.name}(${argsStr})`);
				}
			}

			if (textParts.length > 0) {
				parts.push(`[Assistant]: ${textParts.join("\n")}`);
			}
			if (toolCalls.length > 0) {
				parts.push(`[Assistant tool calls]: ${toolCalls.join("; ")}`);
			}
		} else if (msg.role === "toolResult") {
			const content = msg.content
				.filter((c): c is { type: "text"; text: string } => c.type === "text")
				.map((c) => c.text)
				.join("");
			if (content) {
				parts.push(`[Tool result]: ${truncateForSummary(content, TOOL_RESULT_MAX_CHARS)}`);
			}
		}
	}

	return parts.join("\n\n");
}

// ============================================================================
// Summarization System Prompt
// ============================================================================

export const SUMMARIZATION_SYSTEM_PROMPT = `You are a context summarization assistant. Your task is to read a conversation between a user and an AI assistant, then produce a structured summary following the exact format specified.

Do NOT continue the conversation. Do NOT respond to any questions in the conversation. ONLY output the structured summary.`;

// ============================================================================
// Line Range Helpers
// ============================================================================

/** Collapse a sparse set of line numbers into ascending contiguous inclusive ranges. */
export function contiguousRanges(lines: ReadonlySet<number>): LineRange[] {
	const sorted = [...lines].sort((left, right) => left - right);
	const ranges: LineRange[] = [];
	for (const line of sorted) {
		const last = ranges[ranges.length - 1];
		if (last && line === last.end + 1) last.end = line;
		else ranges.push({ start: line, end: line });
	}
	return ranges;
}
